import { useCallback, useEffect, useState } from "react";

import { apiFetch } from "@/lib/api-client";
import { Skeleton, SkeletonCard } from "@/components/skeleton";

interface DeploymentRun {
  id: string;
  name: string;
  workflowId: string;
  status: string;
  conclusion: string | null;
  sha: string;
  branch: string;
  event: string;
  url: string;
  runNumber: number;
  createdAt: string;
  updatedAt: string;
}

function DeploymentSkeleton() {
  return (
    <div className="space-y-6">
      <div>
        <Skeleton className="h-8 w-52" />
        <Skeleton className="mt-2 h-4 w-80" />
      </div>
      <div className="grid gap-4 md:grid-cols-3">
        {Array.from({ length: 3 }).map((_, index) => (
          <SkeletonCard key={index} className="h-24" />
        ))}
      </div>
      <Skeleton className="h-72 w-full rounded-lg" />
    </div>
  );
}

const STATUS_STYLES: Record<string, string> = {
  completed: "bg-muted text-muted-foreground",
  in_progress: "bg-[var(--color-primary)]/10 text-[var(--color-primary)]",
  queued: "bg-[var(--color-warning)]/10 text-[var(--color-warning)]",
};

const CONCLUSION_STYLES: Record<string, string> = {
  success: "text-[var(--color-success)]",
  failure: "text-[var(--color-destructive)]",
  cancelled: "text-[var(--color-warning)]",
  neutral: "text-muted-foreground",
};

function formatRelativeTime(iso: string) {
  const diff = Date.now() - new Date(iso).getTime();
  if (diff < 60_000) return "just now";
  if (diff < 3_600_000) return `${Math.floor(diff / 60_000)}m ago`;
  if (diff < 86_400_000) return `${Math.floor(diff / 3_600_000)}h ago`;
  return `${Math.floor(diff / 86_400_000)}d ago`;
}

function getRunStateLabel(run: DeploymentRun) {
  if (run.status !== "completed") {
    return run.status;
  }

  return run.conclusion ?? "completed";
}

export default function DeploymentDashboardPage() {
  const [deployments, setDeployments] = useState<DeploymentRun[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isTriggering, setIsTriggering] = useState(false);
  const [selectedRunId, setSelectedRunId] = useState<string | null>(null);

  const loadDeployments = useCallback(async () => {
    try {
      const response = await apiFetch<{ deployments: DeploymentRun[] }>("/api/admin/operations/deployments?limit=20");
      setDeployments(response.deployments);
      setSelectedRunId((current) => current ?? response.deployments[0]?.id ?? null);
      setError(null);
    } catch {
      setDeployments([]);
      setSelectedRunId(null);
      setError("Deployment history is currently unavailable.");
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    void loadDeployments();
  }, [loadDeployments]);

  const selectedRun = deployments.find((run) => run.id === selectedRunId) ?? deployments[0] ?? null;

  const triggerDeploy = async () => {
    setIsTriggering(true);
    try {
      await apiFetch("/api/admin/operations/deployments/trigger", {
        method: "POST",
      });
      await loadDeployments();
    } catch {
      setError("Deploy trigger is currently unavailable.");
    } finally {
      setIsTriggering(false);
    }
  };

  if (isLoading) {
    return <DeploymentSkeleton />;
  }

  return (
    <div className="space-y-6">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-foreground">Deployments</h1>
          <p className="mt-1 text-sm text-muted-foreground">
            Mounted GitHub Actions deploy history for the `deploy-workers` and `deploy-bun-workers` workflows.
          </p>
        </div>
        <button
          onClick={() => void triggerDeploy()}
          disabled={isTriggering}
          className="rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground disabled:opacity-50"
        >
          {isTriggering ? "Triggering..." : "Trigger Deploy"}
        </button>
      </div>

      {error ? (
        <div className="rounded-lg border border-border bg-card p-4">
          <h2 className="text-base font-semibold text-foreground">Deployments unavailable</h2>
          <p className="mt-2 text-sm text-muted-foreground">{error}</p>
          <button
            onClick={() => {
              setIsLoading(true);
              void loadDeployments();
            }}
            className="mt-4 rounded-md border border-border px-4 py-2 text-sm"
          >
            Retry
          </button>
        </div>
      ) : null}

      <div className="grid gap-4 md:grid-cols-3">
        <div className="rounded-lg border border-border bg-card p-4">
          <p className="text-xs text-muted-foreground">Recent deploy runs</p>
          <p className="mt-1 text-2xl font-bold text-foreground">{deployments.length}</p>
          <p className="text-xs text-muted-foreground">GitHub Actions runs on the mounted deploy workflows.</p>
        </div>
        <div className="rounded-lg border border-border bg-card p-4">
          <p className="text-xs text-muted-foreground">Latest branch</p>
          <p className="mt-1 text-2xl font-bold text-foreground">{deployments[0]?.branch ?? "—"}</p>
          <p className="text-xs text-muted-foreground">Newest run by `createdAt` after workflow filtering.</p>
        </div>
        <div className="rounded-lg border border-border bg-card p-4">
          <p className="text-xs text-muted-foreground">Latest SHA</p>
          <p className="mt-1 text-2xl font-bold text-foreground font-mono">{deployments[0]?.sha.slice(0, 7) ?? "—"}</p>
          <p className="text-xs text-muted-foreground">Head commit of the most recent deploy workflow run.</p>
        </div>
      </div>

      <div className="grid gap-6 lg:grid-cols-[minmax(0,1fr)_20rem]">
        <div className="rounded-lg border border-border bg-card">
          <div className="border-b border-border px-4 py-3">
            <h2 className="text-sm font-semibold text-foreground">Recent workflow runs</h2>
          </div>
          {deployments.length === 0 ? (
            <div className="px-4 py-8 text-sm text-muted-foreground">No deployment runs are available yet.</div>
          ) : (
            <div className="divide-y divide-border">
              {deployments.map((run) => (
                <button
                  key={run.id}
                  type="button"
                  onClick={() => setSelectedRunId(run.id)}
                  className={`stagger-item flex w-full items-start justify-between gap-4 px-4 py-4 text-left hover:bg-muted/30 ${
                    selectedRun?.id === run.id ? "bg-primary/5" : ""
                  }`}
                >
                  <div className="min-w-0 flex-1">
                    <div className="flex flex-wrap items-center gap-2">
                      <span className="font-medium text-foreground">{run.name}</span>
                      <span className={`rounded px-2 py-0.5 text-[10px] uppercase tracking-wide ${STATUS_STYLES[run.status] ?? STATUS_STYLES.completed}`}>
                        {run.status}
                      </span>
                      <span className={`text-xs font-medium ${CONCLUSION_STYLES[run.conclusion ?? "neutral"] ?? CONCLUSION_STYLES.neutral}`}>
                        {getRunStateLabel(run)}
                      </span>
                    </div>
                    <div className="mt-2 flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-muted-foreground">
                      <span>#{run.runNumber}</span>
                      <span>{run.branch}</span>
                      <span className="font-mono">{run.sha.slice(0, 7)}</span>
                      <span>{run.event}</span>
                    </div>
                  </div>
                  <span className="shrink-0 text-xs text-muted-foreground">{formatRelativeTime(run.createdAt)}</span>
                </button>
              ))}
            </div>
          )}
        </div>

        <div className="rounded-lg border border-border bg-card p-4">
          <h2 className="text-sm font-semibold text-foreground">Run detail</h2>
          {selectedRun ? (
            <dl className="mt-4 space-y-3 text-sm">
              <div>
                <dt className="text-xs text-muted-foreground">Workflow</dt>
                <dd className="text-foreground">{selectedRun.name}</dd>
              </div>
              <div>
                <dt className="text-xs text-muted-foreground">Branch</dt>
                <dd className="text-foreground">{selectedRun.branch}</dd>
              </div>
              <div>
                <dt className="text-xs text-muted-foreground">SHA</dt>
                <dd className="font-mono text-foreground">{selectedRun.sha}</dd>
              </div>
              <div>
                <dt className="text-xs text-muted-foreground">Status</dt>
                <dd className="text-foreground">{selectedRun.status}</dd>
              </div>
              <div>
                <dt className="text-xs text-muted-foreground">Conclusion</dt>
                <dd className={CONCLUSION_STYLES[selectedRun.conclusion ?? "neutral"] ?? CONCLUSION_STYLES.neutral}>
                  {selectedRun.conclusion ?? "Pending"}
                </dd>
              </div>
              <div>
                <dt className="text-xs text-muted-foreground">Started</dt>
                <dd className="text-foreground">{new Date(selectedRun.createdAt).toLocaleString()}</dd>
              </div>
              <div>
                <dt className="text-xs text-muted-foreground">Updated</dt>
                <dd className="text-foreground">{new Date(selectedRun.updatedAt).toLocaleString()}</dd>
              </div>
              <div>
                <dt className="text-xs text-muted-foreground">Actions URL</dt>
                <dd>
                  <a href={selectedRun.url} target="_blank" rel="noreferrer" className="text-primary underline-offset-2 hover:underline">
                    Open run
                  </a>
                </dd>
              </div>
            </dl>
          ) : (
            <p className="mt-4 text-sm text-muted-foreground">Select a deployment run to inspect its detail.</p>
          )}

          <div className="mt-6 rounded-md border border-border bg-muted/20 p-3">
            <p className="text-xs font-medium text-foreground">Boundary</p>
            <p className="mt-1 text-xs text-muted-foreground">
              Rollback, per-server release inventory, and worker-level release control are not part of the mounted `D-022` contract yet, so this page does not fake them.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
